import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent } from '@/components/shadcn/sheet';
import { getHostMemberDetail } from '@/services/communityHostService';
import { communityHostKeys, type HostListItem } from '@/types/communityHost';
import { openExternalUrl } from '@/utils/openExternalUrl';
import showToast from '@/components/ui/Toast';
import { formatDateFull, formatMonthYear, formatWhatsAppUrl, getInitials } from './hostFormat';

interface HostMemberProfileSheetProps {
  host: HostListItem | null;
  slug: string;
  onClose: () => void;
}

export function HostMemberProfileSheet({ host, slug, onClose }: HostMemberProfileSheetProps) {
  const navigate = useNavigate();
  const memberId = host?.member_id ?? 0;
  const { data, isLoading } = useQuery({
    queryKey: communityHostKeys.memberDetail(slug, memberId),
    queryFn: () => getHostMemberDetail(slug, memberId),
    enabled: Boolean(host && slug && memberId),
  });

  const name = data?.name || host?.name || 'Anggota';
  const phone = data?.phone || '';
  const email = data?.email || '';
  const avatar = data?.avatar || host?.avatar;

  return (
    <Sheet open={!!host} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="rounded-t-3xl border-t border-gray-100 max-h-[90vh] overflow-y-auto px-5 pt-5 pb-8"
      >
        {isLoading ? (
          <div className="h-48 flex items-center justify-center text-xs text-gray-400">Memuat profil...</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {avatar ? (
                  <img src={avatar} alt={name} className="w-14 h-14 rounded-full object-cover border border-gray-100" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#FC480D] text-white font-bold text-lg flex items-center justify-center">
                    {getInitials(name)}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="text-base font-extrabold text-[#0E1726] truncate">{name}</h3>
                  <p className="text-xs text-gray-400 font-medium">
                    Gabung {formatMonthYear(data?.join_date)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center cursor-pointer"
                aria-label="Tutup"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            <div className="bg-gray-50/80 border border-gray-100 rounded-2xl p-3.5 grid grid-cols-3 divide-x divide-gray-200/80 text-center">
              <Stat value={data?.event_count ?? 0} label="Event ikut" />
              <Stat value={`${data?.keaktifan_percentage ?? 0}%`} label="Keaktifan" />
              <Stat value={`${data?.win_rate ?? 0}%`} label="Winrate" />
            </div>

            <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden divide-y divide-gray-100">
              <DetailRow icon="ph:phone-bold" label="NOMOR HP" value={phone || '-'} />
              <DetailRow icon="ph:envelope-simple-bold" label="EMAIL" value={email || '-'} />
              <DetailRow icon="ph:calendar-blank-bold" label="BERGABUNG" value={formatMonthYear(data?.join_date)} />
              <DetailRow
                icon="ph:clock-counter-clockwise-bold"
                label="TERAKHIR IKUT EVENT"
                value={
                  data?.last_event_name
                    ? `${formatDateFull(data.last_event_date)} · ${data.last_event_name}`
                    : '-'
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                type="button"
                onClick={() => {
                  const username = data?.username || data?.nickname;
                  onClose();
                  if (username) navigate(`/profile/${username}`);
                  else showToast.error('Profil pemain tidak tersedia');
                }}
                className="h-12 rounded-full border border-gray-300 text-gray-800 text-xs font-bold flex items-center justify-center gap-2 cursor-pointer"
              >
                <Icon icon="ph:user-bold" className="w-4 h-4" />
                Lihat Profile
              </button>
              {phone ? (
                <button
                  type="button"
                  onClick={() => { void openExternalUrl(formatWhatsAppUrl(phone)); }}
                  className="h-12 rounded-full bg-[#FC480D] text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Icon icon="ph:whatsapp-logo-fill" className="w-4.5 h-4.5" />
                  Hubungi
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  className="h-12 rounded-full bg-gray-200 text-gray-400 text-xs font-bold cursor-not-allowed"
                >
                  Hubungi
                </button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center px-1">
      <span className="text-lg font-extrabold text-[#0E1726]">{value}</span>
      <span className="text-[11px] font-medium text-gray-400 mt-0.5">{label}</span>
    </div>
  );
}

function DetailRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="p-3.5 flex items-center gap-3">
      <div className="w-8 h-8 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center shrink-0 border border-gray-100">
        <Icon icon={icon} className="w-4 h-4" />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">{label}</span>
        <span className="text-xs font-bold text-[#0E1726] truncate">{value}</span>
      </div>
    </div>
  );
}
